const FORM_SELECTOR = 'form[data-atelea-email-signup]';
const ERROR_SELECTOR = '[data-atelea-email-error]';
const SUCCESS_SELECTOR = '[data-atelea-email-success]';
const MESSAGE_TEXT_SELECTOR = '[data-atelea-email-message-text]';
const CHALLENGE_PATH = '/challenge';

/** @param {HTMLFormElement} form @param {boolean} submitting */
function setSubmitting(form, submitting) {
  form.dataset.ateleaEmailSubmitting = String(submitting);
  form.setAttribute('aria-busy', String(submitting));
  form.querySelectorAll('button[type="submit"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = submitting || form.dataset.ateleaEmailComplete === 'true';
    button.setAttribute('aria-busy', String(submitting));
  });
}

/** @param {HTMLFormElement} form */
function resetMessages(form) {
  form.querySelectorAll(`${ERROR_SELECTOR}, ${SUCCESS_SELECTOR}`).forEach((message) => {
    if (message instanceof HTMLElement) message.hidden = true;
  });

  const input = form.querySelector('input[type="email"]');
  if (!(input instanceof HTMLInputElement)) return;
  input.removeAttribute('aria-invalid');
  input.removeAttribute('aria-describedby');
}

/** @param {HTMLFormElement} form @param {string} message */
function showError(form, message) {
  const error = form.querySelector(ERROR_SELECTOR);
  const input = form.querySelector('input[type="email"]');
  if (!(error instanceof HTMLElement)) return;

  const messageText = error.querySelector(MESSAGE_TEXT_SELECTOR);
  if (messageText) messageText.textContent = message;
  error.hidden = false;
  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-invalid', 'true');
    if (error.id) input.setAttribute('aria-describedby', error.id);
  }
  error.focus({ preventScroll: true });
}

/** @param {HTMLFormElement} form */
function showSuccess(form) {
  const success = form.querySelector(SUCCESS_SELECTOR);
  const input = form.querySelector('input[type="email"]');
  if (!(success instanceof HTMLElement)) return;

  form.reset();
  form.dataset.ateleaEmailComplete = 'true';
  success.hidden = false;
  if (input instanceof HTMLInputElement && success.id) {
    input.setAttribute('aria-describedby', success.id);
  }
  success.focus({ preventScroll: true });
  form.dispatchEvent(new CustomEvent('atelea:email:success', { bubbles: true }));
}

/** @param {Document} responseDocument @param {string} formId */
function getResponseForm(responseDocument, formId) {
  const responseForm = responseDocument.getElementById(formId);
  return responseForm instanceof HTMLFormElement ? responseForm : null;
}

/** @param {Document} responseDocument @param {string} formId */
function getResponseError(responseDocument, formId) {
  const message = getResponseForm(responseDocument, formId)?.querySelector(
    `${ERROR_SELECTOR}:not([hidden]) ${MESSAGE_TEXT_SELECTOR}`
  );
  return message?.textContent?.trim() || '';
}

/**
 * @param {Response} response
 * @param {Document} responseDocument
 * @param {string} formId
 */
function isSuccessful(response, responseDocument, formId) {
  const responseUrl = new URL(response.url, window.location.origin);
  if (responseUrl.searchParams.get('customer_posted') === 'true') return true;

  return Boolean(
    getResponseForm(responseDocument, formId)?.querySelector(
      `${SUCCESS_SELECTOR}:not([hidden])`
    )
  );
}

/** @param {Response} response @param {Document} responseDocument */
function isChallenge(response, responseDocument) {
  const responseUrl = new URL(response.url, window.location.origin);
  return (
    responseUrl.pathname.startsWith(CHALLENGE_PATH) ||
    Boolean(responseDocument.querySelector('.shopify-challenge__container'))
  );
}

/** @param {HTMLFormElement} form */
async function submitForm(form) {
  try {
    const response = await fetch(form.action, {
      method: form.method || 'post',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    const responseText = await response.text();
    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');

    if (isChallenge(response, responseDocument)) {
      showError(
        form,
        'Shopify necesita verificar este registro. Inténtalo nuevamente.'
      );
      return;
    }

    if (response.ok && isSuccessful(response, responseDocument, form.id)) {
      showSuccess(form);
      return;
    }

    const responseError = getResponseError(responseDocument, form.id);
    showError(
      form,
      responseError || 'No pudimos completar el registro. Inténtalo nuevamente.'
    );
  } catch {
    showError(
      form,
      'No pudimos conectar con Shopify. Revisa tu conexión e inténtalo nuevamente.'
    );
  } finally {
    setSubmitting(form, false);
  }
}

/** @param {HTMLFormElement} form */
function initializeForm(form) {
  if (form.dataset.ateleaEmailInitialized === 'true') return;

  // Shopify's hCaptcha wrapper calls this method after adding its token.
  form.submit = () => {
    void submitForm(form);
  };
  form.dataset.ateleaEmailInitialized = 'true';
}

/** @param {HTMLFormElement} form */
function submitProtected(form) {
  resetMessages(form);
  setSubmitting(form, true);
  initializeForm(form);

  /** @type {{ protect?: (form: HTMLFormElement, callback: () => void) => void } | undefined} */
  const captcha = Reflect.get(window.Shopify, 'captcha');
  if (typeof captcha?.protect !== 'function') {
    void submitForm(form);
    return;
  }

  captcha.protect(form, () => form.submit());
}

/** @param {SubmitEvent} event */
function handleSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  if (
    form.dataset.ateleaEmailSubmitting === 'true' ||
    form.dataset.ateleaEmailComplete === 'true'
  ) {
    return;
  }
  submitProtected(form);
}

/** @param {Event} event */
function initializeInteractedForm(event) {
  const input = event.target;
  if (!(input instanceof HTMLElement)) return;

  const form = input.closest(FORM_SELECTOR);
  if (form instanceof HTMLFormElement) initializeForm(form);
}

function initializeForms() {
  document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
    if (form instanceof HTMLFormElement) initializeForm(form);
  });
  restorePostedForm();
}

function restorePostedForm() {
  const responseUrl = new URL(window.location.href);
  if (responseUrl.searchParams.get('customer_posted') !== 'true') return;

  const formId = decodeURIComponent(responseUrl.hash.slice(1));
  const form = document.getElementById(formId);
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

  resetMessages(form);
  showSuccess(form);
}

window.addEventListener('submit', handleSubmit, { capture: true });
window.addEventListener('focusin', initializeInteractedForm, { capture: true });
window.addEventListener('change', initializeInteractedForm, { capture: true });
document.addEventListener('DOMContentLoaded', initializeForms);
